import { Alert, Box, CircularProgress } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import { patientDetailLinkFor, patientsLink } from '../../../library/constants';
import { toDateInputValue } from '../../../library/dateTimeHelper';
import { IPatient } from '../../../library/models/clinic';
import {
    EvaluationFormValues,
    IEvaluation,
    IEvaluationSummary,
    IGdprConsent,
    toFollowUpFormValues,
} from '../../../library/models/evaluation';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactInfiniteQuery, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { alertStyles, loadingBoxStyles } from './evaluation.styles';
import { EvaluationWizard } from './EvaluationWizard';

/**
 * Today, as an ISO date.
 *
 * Through `toDateInputValue` rather than `toISOString().slice(0, 10)`, which reads the
 * date off the *UTC* clock: anywhere west of Greenwich, an evaluation opened in the
 * evening would have been dated tomorrow.
 */
const today = (): string => toDateInputValue(new Date());

const defaultValues = (): EvaluationFormValues => ({
    emailAddress: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    occupation: '',
    age: '',
    sex: '',
    evaluatedOn: today(),
    reasonForVisit: '',
    investigations: '',
    painLevel: '',
    painCharacter: '',
    medication: '',
    injuryHistory: '',
    notes: '',
    bodyDiagram: [],
    consentCapacity: 'PATIENT',
    childAge: '',
    patientAddress: '',
    idSeries: '',
    idNumber: '',
    referringDoctor: '',
    treatmentConsentGiven: false,
    signedName: '',
    signature: '',
    gdprConsentGiven: false,
});

/** One row is all this needs: the API answers newest first, so page 0 is the last sheet. */
const LAST_EVALUATION_ONLY = 1;

/**
 * The evaluation wizard, and which patient it is being filled in for.
 *
 * <p>Two routes land here. '/patients/new-evaluation' is how a stranger joins the clinic:
 * a blank form, where the address they type is what creates their account. The other,
 * '/patients/:patientId/new-evaluation', is a returning patient's next sheet -- reached
 * from their own file, and pre-filled from it.
 *
 * <p>The seed is resolved *before* the wizard mounts rather than reset into it afterwards.
 * A form whose defaults arrive late has to overwrite what is on screen to apply them, and
 * a therapist who started typing during the fetch would watch it happen.
 */
export const Evaluation = (): JSX.Element => {
    const navigate: NavigateFunction = useNavigate();
    const { t: translate } = useTranslation();
    const { patientId: patientIdParam } = useParams<{ patientId: string }>();

    const patientId = Number(patientIdParam);
    /** No id in the path is the blank form, not a bad id: the other route has no param. */
    const isFollowUp = patientIdParam !== undefined;
    const canFetch = isFollowUp && Number.isFinite(patientId);

    const { data: patient, isLoading: isLoadingPatient } = useReactQuery<IPatient>(
        services.users.getPatient(patientId),
        // A non-numeric id in the URL would otherwise become "/users/patients/NaN".
        { enabled: canFetch }
    );

    const { data: history, isLoading: isLoadingHistory } =
        useReactInfiniteQuery<IEvaluationSummary>(
            services.evaluations.getEvaluationsForPatient(patientId, LAST_EVALUATION_ONLY),
            { enabled: canFetch }
        );

    /** Absent for a patient an admin invited and nobody has evaluated yet. */
    const lastEvaluationId: number | undefined = history?.pages[0]?.items[0]?.id;

    const { data: lastEvaluation, isLoading: isLoadingLast } = useReactQuery<IEvaluation>(
        // Number(undefined) is NaN, and `enabled` is what stops it being requested.
        services.evaluations.getEvaluation(lastEvaluationId as number),
        { enabled: lastEvaluationId !== undefined }
    );

    /**
     * The data-processing agreement is given once per patient per clinic, so the wizard
     * asks whether there is one before deciding to collect it again.
     *
     * Its own request rather than a field on the patient or on the last sheet: it belongs
     * to neither. `IPatient` is the shape a row of the paged patient list has, and a
     * clinical fact does not belong in a directory a search pages through; the sheet is
     * the wrong owner too, since the agreement outlives any one of them.
     */
    const { data: gdprConsent, isLoading: isLoadingConsent } = useReactQuery<IGdprConsent | null>(
        services.evaluations.getGdprConsent(patientId),
        { enabled: canFetch }
    );

    if (!isFollowUp) {
        return (
            <EvaluationWizard
                initialValues={defaultValues()}
                isFollowUp={false}
                requiresGdprConsent
                onSaved={() => navigate(patientsLink)}
            />
        );
    }

    if (isLoadingPatient || isLoadingHistory || isLoadingLast || isLoadingConsent) {
        return (
            <Box sx={loadingBoxStyles}>
                <CircularProgress size={40} />
            </Box>
        );
    }

    // Only the patient is worth refusing over. A missing history is the ordinary
    // zero-evaluations case, and their identity alone still saves the retype -- but a
    // patient who could not be resolved is exactly what the locked address exists to
    // prevent, so there is nothing safe to pre-fill and nothing to lock it to.
    if (patient === undefined) {
        return (
            <Alert severity="error" sx={alertStyles}>
                {translate(ResourcesIds.EVALUATION_PATIENT_LOAD_ERROR)}
            </Alert>
        );
    }

    return (
        <EvaluationWizard
            initialValues={toFollowUpFormValues(patient, lastEvaluation, today())}
            isFollowUp
            // Null is an answer, not a gap: a patient an admin invited and nobody has
            // evaluated has given nothing, and they are asked exactly like a stranger.
            // This is why the flag is not `isFollowUp` -- that only says the URL carried
            // an id, and keying off it would skip an agreement nobody ever gave.
            requiresGdprConsent={!gdprConsent}
            onSaved={() => navigate(patientDetailLinkFor(patient.id))}
        />
    );
};
